import { useMemo } from 'react';
import useSWR from 'swr';
import { defaultContent } from './defaults';

export const CONTENT_KEY = '/api/public/content';

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Overlays CMS values on the built-in defaults. Objects merge field by field;
 * arrays and text are replaced whole so an admin can remove list items or
 * clear a field. Missing or null overrides keep the default.
 */
export function mergeContent(base, override) {
  if (override === undefined || override === null) return base;
  if (isPlainObject(base) && isPlainObject(override)) {
    const out = { ...base };
    for (const key of Object.keys(override)) out[key] = mergeContent(base[key], override[key]);
    return out;
  }
  return override;
}

const fetchContent = (url) =>
  fetch(url, { headers: { Accept: 'application/json' } }).then((res) => (res.ok ? res.json() : { pages: {} }));

/** All CMS rows keyed by page_key. Empty until the API answers; defaults cover the gap. */
export function useCmsPages() {
  const { data } = useSWR(CONTENT_KEY, fetchContent, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    fallbackData: { pages: {} },
  });
  return (data && data.pages) || {};
}

/** Content for one page (defaults overlaid with the admin's edits). */
export function usePage(pageKey) {
  const pages = useCmsPages();
  const stored = pages[pageKey];
  return useMemo(() => mergeContent(defaultContent[pageKey] || {}, stored), [pageKey, stored]);
}

export const toPhoneHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;
export const toWhatsappHref = (phone) => `https://wa.me/${String(phone || '').replace(/\D/g, '')}`;

/** Site-wide settings page: company identity, branding, footer text and the call-to-action banner. */
export function useSettings() {
  return usePage('settings');
}

/** Company identity with derived phone / WhatsApp links. */
export function useCompany() {
  const { company } = useSettings();
  return useMemo(
    () => ({
      ...company,
      phoneHref: toPhoneHref(company.phone),
      phone2Href: toPhoneHref(company.phone2),
      whatsappHref: toWhatsappHref(company.whatsapp || company.phone),
    }),
    [company],
  );
}

export { defaultContent };
